import { FunctionComponent, useMemo } from "react"
import { FormI } from "./types"
import styles from "./organizationMembershipAccessUpdate.module.scss"
import { Toggle } from "../shared/selectionControls"
import MultiSelect, {
    ReactMultiSelectOptionT,
} from "../shared/input/react-select/multiSelect"
import { ActionMeta } from "react-select"
import { MultiValue } from "react-select"
import { SubjectRole } from "../../generated/graphql"
import classNames from "classnames"
import { RolesUiDisplayObj } from "../../util/subjectRole"
import Button from "../../components/shared/button"
import Policy_OrganizationMembership from "../../rolePolicy/organizationMembership"

interface FormProps {
    form: FormI
    handleManagedAccessChange: (value: boolean) => void
    handleWellPermissionsChange: (
        newValue: MultiValue<ReactMultiSelectOptionT>,
        actionMeta: ActionMeta<ReactMultiSelectOptionT>
    ) => void
    handleSelectAllWells: (updatedSelect: boolean) => void
    targetSubjectName: string
    targetSubjectRole: SubjectRole
}

const Form: FunctionComponent<FormProps> = ({
    form,
    targetSubjectName,
    targetSubjectRole,
    handleManagedAccessChange,
    handleWellPermissionsChange,
    handleSelectAllWells,
}) => {
    const canHaveManagedAccess =
        Policy_OrganizationMembership.canHaveManagedAccessOrganizationMembership(
            targetSubjectRole
        )

    const areAllWellsSelected = useMemo(() => {
        return form.wellPermissions.every((well) => well.isSelected)
    }, [form.wellPermissions])

    const text = canHaveManagedAccess
        ? "Use the toggle to restrict access to specific wells. Users with limited access will only be able to view and interact with wells they have permission for."
        : "Admins can not have limited access. Only members of role organization member can have limited access."

    return (
        <div className={styles.formContainer}>
            <div className={styles.subjectContainer}>
                <div
                    className={classNames(
                        styles.marginRight8,
                        styles.nameContainer
                    )}
                >
                    <span className={classNames(styles.formLabel)}>User:</span>
                    <span className={styles.formValue}>
                        {targetSubjectName}
                    </span>
                </div>

                <div>
                    <span className={classNames(styles.formLabel)}>Role:</span>
                    <span
                        className={classNames(
                            styles.formValue,
                            styles.marginRight8
                        )}
                    >
                        {RolesUiDisplayObj[targetSubjectRole]}
                    </span>
                </div>
            </div>

            <div className={styles.info}>{text}</div>
            <div className={styles.toggleContainer}>
                <Toggle
                    checked={form.fullAccess}
                    handleToggleClick={() =>
                        handleManagedAccessChange(!form.fullAccess)
                    }
                    toggleLabel=""
                    disabled={!canHaveManagedAccess}
                />
                <div className={styles.toggleLabel}>
                    <div
                        className={classNames(
                            styles.formLabel,
                            styles.marginLeft8
                        )}
                    >
                        Access:
                    </div>
                    {form.fullAccess
                        ? `${targetSubjectName}  will have access to all wells.`
                        : "Limited"}
                </div>
            </div>
            {!form.fullAccess && (
                <div>
                    <div
                        className={classNames(
                            styles.formLabel,
                            styles.marginBottom8
                        )}
                    >
                        Select the wells {targetSubjectName} will have access
                        to:
                    </div>
                    <div className={styles.flexRow}>
                        <MultiSelect
                            options={form.wellPermissions}
                            onChange={handleWellPermissionsChange}
                            isClearable={true}
                        />
                        <Button
                            handleButtonClick={() => {
                                const updatedSelect = !areAllWellsSelected
                                return handleSelectAllWells(updatedSelect)
                            }}
                            status="secondary"
                            condition="default"
                        >
                            {areAllWellsSelected
                                ? "Deselect all wells"
                                : "Select all wells"}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default Form
